import { accommodationQuantityZodObject } from "@@/shared/acquire/accommodation-quantity";
import { accommodationRateIdZodSchema } from "@@/shared/acquire/accommodation-rate-id";
import { bookableOptionIdZodSchema } from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import { resourceReferenceIdZodSchema } from "@@/shared/acquire/resource-references/resource-reference-id";
import { pickedPickedAccommodationTypeZodObject } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { resourceIdZodSchema } from "@@/shared/acquire/resources/resource-id";
import { dateRangeZodObject } from "@@/shared/date-time/date-range";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { contactInfoZodObject } from "@@/shared/locations/contact-info";
import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { priceZodSchema } from "@@/shared/prices/price";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerSlugZodSchema } from "@@/shared/providers/provider";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { recipientsZodObject } from "@@/shared/quotes/quote-base";
import { quoteIdZodSchema } from "@@/shared/quotes/quote-id";
import {
    QuoteItemIdFactory,
    quoteItemIdZodSchema,
} from "@@/shared/quotes/quote-item-id";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { quantityZodSchema } from "@@/shared/units-and-quantities/quantity";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";

const subBasicQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),

    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("BASIC"),

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema, //Do we need two prices?

    references: z.object({
        parentItemId: quoteItemIdZodSchema.optional(),
        //All other references?
    }),
});

const subBuyableQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),

    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("BUYABLE"),

    //Subtype of buyable, food, meal, service, etc??

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema, //Do we need two prices?

    references: z.object({
        parentItemId: quoteItemIdZodSchema.optional(),
        productId: productIdZodSchema.optional(),
        //All other references?
    }),
});

const subBookableQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),

    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("BOOKABLE"),

    //Subtype of buyable, food, meal, service, etc??

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema, //Do we need two prices?

    references: z.object({
        productId: productIdZodSchema.optional(),
        parentItemId: quoteItemIdZodSchema.optional(),
        //All other references?
    }),
});

const subBookableQuantityQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),

    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("BOOKABLE_QUANTITY"),

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema, //Do we need two prices?

    references: z.object({
        parentItemId: quoteItemIdZodSchema,
        bookableOptionId: bookableOptionIdZodSchema,
        referenceId: resourceReferenceIdZodSchema,

        productId: productIdZodSchema.optional(),
        //All other references?
    }),
});

// Define the accommodation quote item
const subAccommodationQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("ACCOMMODATION"),

    title: translatableZodSchema,

    // Base pricing and quantity
    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema,

    // Accommodation specific data
    time: timeRangeZodSchema,
    quantities: z.array(
        z.object({
            type: accommodationQuantityZodObject,
            quantity: quantityZodSchema,
        }),
    ),

    // Accommodation details
    accommodation: z.object({
        title: translatableZodSchema,
        resourceId: resourceIdZodSchema,
        rateId: accommodationRateIdZodSchema,
        type: pickedPickedAccommodationTypeZodObject,
    }),

    references: z.object({
        parentItemId: quoteItemIdZodSchema.optional(),
        productId: productIdZodSchema,
    }),
});

// Define accommodation item types
type SubAccommodationQuoteItem = z.infer<
    typeof subAccommodationQuoteItemZodObject
>;

type SubAccommodationQuoteItemFactoryParams = Except<
    SubAccommodationQuoteItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

// Factory function for creating accommodation quote items
const subAccommodationQuoteItemCommandFactory = (
    params: SubAccommodationQuoteItemFactoryParams,
): SubAccommodationQuoteItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(),
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

// Accommodation rate item for quotes
const subAccommodationRateQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("ACCOMMODATION_RATE_ITEM"),

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema,

    time: timeRangeZodSchema,
    rateId: accommodationRateIdZodSchema,

    references: z.object({
        parentItemId: quoteItemIdZodSchema,
        productId: productIdZodSchema.optional(),
    }),
});

type SubAccommodationRateQuoteItem = z.infer<
    typeof subAccommodationRateQuoteItemZodObject
>;

type SubAccommodationRateQuoteItemFactoryParams = Except<
    SubAccommodationRateQuoteItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const subAccommodationRateQuoteItemCommandFactory = (
    params: SubAccommodationRateQuoteItemFactoryParams,
): SubAccommodationRateQuoteItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(),
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

const resourceQuoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("LINE_ITEM"),
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    type: z.literal("RESOURCE"),

    title: translatableZodSchema,

    quantity: z.number().min(1),
    price: priceZodSchema,
    pricePerItem: priceZodSchema, //Do we need two prices?
    resourceId: resourceIdZodSchema,
    references: z.object({
        parentItemId: quoteItemIdZodSchema.optional(),
    }),
});

const subQuoteItemZodSchema = z.union([
    subBasicQuoteItemZodObject,
    resourceQuoteItemZodObject,
    subBookableQuantityQuoteItemZodObject,
    subBuyableQuoteItemZodObject,
    subBookableQuoteItemZodObject,

    subAccommodationQuoteItemZodObject,
    subAccommodationRateQuoteItemZodObject,
]);

// BASIC QUOTE ITEM
const quoteItemZodObject = z.object({
    _id: quoteItemIdZodSchema,
    _type: z.literal("QUOTE_ITEM"),
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    meta: metaContentZodSchema,

    //Optional/mandatory?
    time: timeRangeZodSchema,

    references: z.object({
        //Used?
        productId: productIdZodSchema.optional(),
    }),

    subItems: z.array(subQuoteItemZodSchema),
});

type HeadQuoteItem = z.infer<typeof quoteItemZodObject>;
const headQuoteItemZodSchema: z.ZodSchema<HeadQuoteItem> = quoteItemZodObject;

type SubBasicItem = z.infer<typeof subQuoteItemZodSchema>;

type SubBasicItemFactoryParams = Except<
    SubBasicItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const subBasicItemCommandFactory = (
    params: SubBasicItemFactoryParams,
): SubBasicItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(), // Or however you generate IDs
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

type SubResourceItem = z.infer<typeof resourceQuoteItemZodObject>;

type SubResourceItemFactoryParams = Except<
    SubResourceItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const subResourceItemCommandFactory = (
    params: SubResourceItemFactoryParams,
): SubResourceItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(), // Or however you generate IDs
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

type SubBookableQuoteItem = z.infer<typeof subBookableQuoteItemZodObject>;

type SubBookableItemFactoryParams = Except<
    SubBookableQuoteItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const subBookableItemCommandFactory = (
    params: SubBookableItemFactoryParams,
): SubBookableQuoteItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(), // Or however you generate IDs
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

type SubBookableQuantityQuoteItem = z.infer<
    typeof subBookableQuantityQuoteItemZodObject
>;

type SubBookableQuantityQuoteItemFactoryParams = Except<
    SubBookableQuantityQuoteItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const subBookableQuantityQuoteItemCommandFactory = (
    params: SubBookableQuantityQuoteItemFactoryParams,
): SubBookableQuantityQuoteItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(), // Or however you generate IDs
        _type: "LINE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

type QuoteItemFactoryParams = Except<
    HeadQuoteItem,
    "_id" | "_type" | "_createdAt" | "_updatedAt"
>;

const quoteItemCommandFactory = (
    params: QuoteItemFactoryParams,
): HeadQuoteItem => {
    return {
        ...params,
        _id: QuoteItemIdFactory(), // Or however you generate IDs
        _type: "QUOTE_ITEM",
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
    };
};

//**
// Projection
//
//  */

const quoteStatusZodObject = z.union([
    z.literal("DRAFT"),
    z.literal("PUBLISHED"),
    z.literal("ACCEPTED"),
    // z.literal("REJECTED"),
    // z.literal("EXPIRED"),
]);

type QuoteStatus = z.infer<typeof quoteStatusZodObject>;

const quoteZodObject = z.object({
    _id: quoteIdZodSchema,
    _type: z.literal("QUOTE"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,

    _createdBy: userIdZodSchema,

    _version: z.number().min(0),

    lastEditVersion: z.number().min(0),

    reference: z.string(),

    providerId: providerIdZodSchema,
    providerSlug: providerSlugZodSchema,

    title: translatableZodSchema,

    state: quoteStatusZodObject,

    valid: dateRangeZodObject,

    //Customers eller participants?
    recipients: z.array(recipientsZodObject),

    /**Figure what is need here, gäller vem det är för */
    customer: contactInfoZodObject,

    items: z.array(headQuoteItemZodSchema),
    //Each time a quote is published,
    published: z.array(
        z.object({
            publishedAt: isoAndUnixZodSchema,
            publishedVersion: z.number().min(0),
        }),
    ),

    accepted: z
        .object({
            name: z.string(),
            email: z.string().email(),
            acceptedAt: isoAndUnixZodSchema.optional(),
            acceptedVersion: z.number().min(0).optional(),
        })
        .optional(),

    viewCount: z.number(),
    viewedLast: isoAndUnixZodSchema.optional(),
});

type Quote = z.infer<typeof quoteZodObject>;
const QuoteZodSchema: z.ZodSchema<Quote> = quoteZodObject;

type QuoteFactoryParams = Except<Quote, "_type">;

const quoteFactory = (params: QuoteFactoryParams): Quote => {
    return {
        ...params,
        _type: "QUOTE",
        _status: "ACTIVE",
    };
};

export {
    headQuoteItemZodSchema,
    quoteFactory,
    quoteItemCommandFactory,
    QuoteZodSchema,
    subAccommodationQuoteItemCommandFactory,
    subAccommodationQuoteItemZodObject,
    subAccommodationRateQuoteItemCommandFactory,
    subAccommodationRateQuoteItemZodObject,
    subBasicItemCommandFactory,
    subBasicQuoteItemZodObject,
    subBookableItemCommandFactory,
    subBookableQuantityQuoteItemCommandFactory,
    subBookableQuantityQuoteItemZodObject,
    subBookableQuoteItemZodObject,
    subQuoteItemZodSchema,
    subResourceItemCommandFactory,
};
export type {
    HeadQuoteItem,
    Quote,
    QuoteStatus,
    SubAccommodationQuoteItem,
    SubAccommodationRateQuoteItem,
    SubBasicItem,
    SubBookableQuantityQuoteItem,
    SubBookableQuoteItem,
    SubResourceItem,
};
